import React, { useRef, useEffect } from "react";
import { useReactToPrint } from "react-to-print";
import styles from "./prescription.module.scss";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getPrescriptionDetails } from "../../../store/slices/appointmentSlice";

const ViewPrescription = () => {
  const pageRef = useRef();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { clinicInfo } = useSelector((state) => state.clinic);
  const { token } = useSelector((state) => state.auth);
  const { prescriptionInfo } = useSelector((state) => state.appointmentSlice);

  const appointmentId = sessionStorage.getItem("appointmentId");

  useEffect(() => {
    if (appointmentId) {
      dispatch(getPrescriptionDetails({ appointmentId, token }));
    }
  }, [appointmentId, dispatch, token]);

  /* PRINT FUNCTION */
  const handlePrint = useReactToPrint({
  contentRef: pageRef,
  documentTitle: "Prescription",
  pageStyle: `
    @page {
      size: A4;
      margin: 0;
    }
    @media print {
      body {
        -webkit-print-color-adjust: exact;
      }
    }
  `,
});

  return (
    <>
      {/* TOP BAR */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="m-0">Prescription</h4>

        <button
          className="btn btn-primary"
          onClick={() => navigate("/appointments")}
        >
          Back to List
        </button>
      </div>

      {/* ACTION BUTTONS */}
      <div className="d-flex gap-2 mb-3">
        <button className="btn btn-success" onClick={handlePrint}>
          Print / Download Prescription
        </button>
      </div>

      {/* PRINT AREA */}
      <div ref={pageRef} className={styles.page}>
        {/* WATERMARK */}
        <div className={styles.watermark}>
          <img
            src={clinicInfo[0]?.hospitalInfo?.waterMark}
            alt="Watermark"
          />
        </div>

        <div className={styles.content}>
          {/* HEADER */}
          <div className={styles.header}>
            <div className={styles.headerLeft}>
              <img
                src={clinicInfo[0]?.hospitalInfo?.pdfHeader}
                alt="Clinic Logo"
                className={styles.logo}
              />
              <div className={styles.tagline}>
                {clinicInfo[0]?.tagline}
              </div>
            </div>

            <div className={styles.headerRight}>
              <span>
                {clinicInfo[0]?.hospitalInfo?.iso_certificate}
              </span>

              <div className={styles.address}>
                <span>{clinicInfo[0]?.clinicAddress?.plot}, {clinicInfo[0]?.clinicAddress?.street}, {clinicInfo[0]?.clinicAddress?.area}, {clinicInfo[0]?.clinicAddress?.city?.name}, {clinicInfo[0]?.clinicAddress?.pin}</span>

                <div className={styles.contactRow}>
                  <span>{clinicInfo[0]?.hospitalInfo?.web_address}</span>
                  <span>{clinicInfo[1]?.phone}</span>
                </div>
              </div>
            </div>
          </div>

          {/* DOCTOR */}
          <div className={styles.card}>
            <div className={styles.cardTitle}>Doctor Details</div>

            <div className={styles.gridTwo}>
              <div>
                <b>Name:</b>{" "}
                {prescriptionInfo?.list?.appointment?.physicianId?.userId?.name}
              </div>

              <div>
                <b>Date:</b>{" "}
                {prescriptionInfo?.list?.appointment?.appDate
                  ?.split("T")[0]
                  ?.split("-")
                  .reverse()
                  .join("/")}
              </div>

              <div>
                <b>Reg. No:</b>{" "}
                {prescriptionInfo?.list?.appointment?.physicianId?.license}
              </div>

              <div>
                <b>Specialization:</b>{" "}
                {
                  prescriptionInfo?.list?.appointment?.physicianId
                    ?.specialization
                }
              </div>
            </div>

            <div className={styles.smallText}>
              {
                prescriptionInfo?.list?.appointment?.physicianId
                  ?.additionalInfo
              }
            </div>
          </div>

          {/* PATIENT */}
          <div className={styles.card}>
            <div className={styles.cardTitle}>Patient Information</div>

            <div className={styles.gridThree}>
              <div>
                <b>Name:</b>{" "}
                {prescriptionInfo?.list?.appointment?.patientId?.userId?.name}
              </div>

              <div>
                <b>UHID:</b>{" "}
                {prescriptionInfo?.list?.appointment?.patientId?.UHID}
              </div>

              <div>
                <b>Age/Gender:</b>{" "}
                {
                  prescriptionInfo?.list?.appointment?.patientId?.userId?.age
                    ?.year
                }
                /
                {prescriptionInfo?.list?.appointment?.patientId?.userId?.gender}
              </div>
            </div>
          </div>

          {/* VITALS */}
          <div className={styles.card}>
            <div className={styles.cardTitle}>Vitals</div>

            <div className={styles.gridFive}>
              <div>Weight:</div>
              <div>Height:</div>
              <div>BP:</div>
              <div>Pulse:</div>
              <div>Temp:</div>
            </div>
          </div>

          {/* NOTES */}
          <div className={styles.noteSection}>
            <div className={styles.noteTitle}>Symptoms:</div>
          </div>

          <div className={styles.noteSection}>
            <div className={styles.noteTitle}>Previous History:</div>
          </div>

          {/* MEDICINE TABLE */}
          <table className={styles.table}>
            <tbody>
              <tr>
                <td></td>
                <td></td>
                <td></td>
              </tr>

              <tr>
                <td></td>
                <td></td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* SIGNATURE */}
        <div className={styles.signatureRow}>
          <div>Follow-up Date: __________</div>

          <div className={styles.signatureBox}>
            {prescriptionInfo?.list?.appointment?.physicianId?.userId?.name}
          </div>
        </div>

        {/* FOOTER */}
        <div className={styles.footer}>
          <div className={styles.footerLeft}>
            <span>{clinicInfo[1]?.clinicName}</span>
            <br />

            <span>
              <b>Email:</b> {clinicInfo[0]?.accountInfo?.accountEmail}
            </span>

            <br />

            <span>
              <b>Address:</b> {clinicInfo[0]?.clinicAddress?.plot}, {clinicInfo[0]?.clinicAddress?.street}, {clinicInfo[0]?.clinicAddress?.area}, {clinicInfo[0]?.clinicAddress?.city?.name}, {clinicInfo[0]?.clinicAddress?.pin}
            </span>

            <br />

            <span className={styles.tnc}>
              <b>T&C: No Charges Within 7 Working Days</b>
            </span>
          </div>

          <div className={styles.footerRight}>
            For appointment & sample collection, contact:{" "}
            {clinicInfo[0]?.phone}
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewPrescription;